import type { DwtfAuth } from "../core/auth.js";
import { getKey } from "../storage/index.js";
import { encodeRecovery, parseRecovery } from "./recovery.js";
import { brandLabel, loadDict, t, type Brand, type Dict } from "./i18n.js";
import { sdkAssetUrl } from "../assets.js";
import {
  openAccountModal,
  openBackupModal,
  openChoiceModal,
  openRestoreModal,
} from "./modals.js";
import { bootModules } from "./remote.js";

const CSS = `:host{all:initial}.wrap{display:flex;gap:10px}button{display:inline-flex;align-items:center;gap:.55em;border:1px solid #fff5;background:#111;color:#fff;padding:.62em 1.1em;cursor:pointer;font:600 14px/1.2 system-ui,sans-serif;border-radius:999px;transition:border-color .15s ease,background .15s ease,opacity .15s ease}button:hover:not(:disabled){border-color:#fff8}button:disabled{opacity:.55;cursor:wait}button.on{background:#00e639;border-color:#00e639;color:#000}button img.dwtf-logo{flex:none;height:1.25em;width:auto;display:block}`;

export type MountAuthOptions = {
  target: HTMLElement;
  auth: DwtfAuth;
  label?: string;
  lang?: string;
  brand?: Brand;
  onSuccess?: (u: { userId: string; token: string }) => void;
  onError?: (e: Error) => void;
};

/**
 * Single-button progressive flow (ADR 0006):
 * key in IDB → silent sign-in → Connected; else choice (create | restore).
 * Connected click → account modal (not re-auth).
 * JWT kept in sessionStorage so MPA navigations (is.ag) keep Connected.
 */
export function mountAuthWidget(opts: MountAuthOptions) {
  let d: Dict = t(opts.lang);
  let brand: Brand | undefined = opts.brand;
  const bl = () => brandLabel(brand, d);
  const h = document.createElement("div");
  h.dataset.dwtf = "auth";
  const s = h.attachShadow({ mode: "open" });
  const logo = sdkAssetUrl("logo.svg");
  s.innerHTML = `<style>${CSS}</style><div class="wrap"><button class="main"></button></div>`;
  const main = s.querySelector("button.main") as HTMLButtonElement;
  opts.target.append(h);

  let afterAuth: ((u: { userId: string; token: string }) => void) | undefined;
  bootModules(
    opts.auth,
    (fn) => {
      afterAuth = fn;
    },
    (c) => {
      if (opts.brand !== undefined) return;
      if (c.brand === "d.wtf" || c.brand === "an.one") {
        brand = c.brand;
        paint();
      }
    },
  );

  // Connected always mirrors auth session — never a local flag that races can clobber.
  const paint = (busy = false) => {
    const expired = opts.auth.isExpired();
    const on = !!opts.auth.getSession() && !expired;
    main.disabled = busy;
    main.classList.toggle("on", on && !busy);
    const label =
      opts.label ??
      (expired
        ? d.relogin
        : on
        ? d.connected
        : bl()
          ? `${d.signIn} ${bl()}`
          : d.signIn);

    const logoEl = `<img class="dwtf-logo" src="${logo}" alt="">`;
    main.innerHTML = `${label} ${logoEl}`;
  };
  paint();
  void loadDict(opts.lang).then((dict) => {
    d = dict;
    paint();
  });

  const fail = (e: unknown) =>
    opts.onError?.(e instanceof Error ? e : new Error(String(e)));
  const ok = (u: { userId: string; token: string }) => {
    paint();
    afterAuth?.(u);
    opts.onSuccess?.(u);
  };

  const restore = () => {
    openRestoreModal({
      lang: opts.lang,
      onRestore: async (raw) => {
        const { publicKeyB64, privateKeyB64 } = parseRecovery(raw);
        ok(await opts.auth.importAndSignIn(privateKeyB64, publicKeyB64));
      },
    });
  };

  const create = async () => {
    paint(true);
    try {
      const res = await opts.auth.createAccount();
      const k = await opts.auth.exportRecoveryKey();
      paint();
      openBackupModal({
        lang: opts.lang,
        recoveryKey: encodeRecovery(k.publicKeyB64, k.privateKeyB64),
        onClose: () => ok({ userId: res.userId, token: res.token }),
      });
    } catch (e) {
      paint();
      fail(e);
    }
  };

  const account = async () => {
    try {
      const k = await opts.auth.exportRecoveryKey();
      openAccountModal({
        lang: opts.lang,
        recoveryKey: encodeRecovery(k.publicKeyB64, k.privateKeyB64),
        onImport: restore,
        onLogout: async () => {
          await opts.auth.logout();
          paint();
        },
      });
    } catch (e) {
      fail(e);
    }
  };

  // Resume: sessionStorage (same tab) or silent challenge if key in IDB.
  // Never leave the button disabled if IDB/API hangs or fails.
  void (async () => {
    try {
      if (opts.auth.getSession() && !opts.auth.isExpired()) {
        paint();
        return;
      }
      if (!(await getKey())) return;
      paint(true);
      ok(await opts.auth.signIn());
    } catch {
      paint();
    }
  })();

  main.onclick = () => {
    void (async () => {
      paint(true);
      try {
        if (opts.auth.isExpired()) {
          ok(await opts.auth.relogin());
          return;
        }
        if (opts.auth.getSession()) {
          paint();
          await account();
          return;
        }
        // getKey() fail-opens to null (private mode / blocked IDB) → choice modal.
        if (await getKey()) {
          ok(await opts.auth.signIn());
          return;
        }
        paint();
        openChoiceModal({
          lang: opts.lang,
          onCreate: () => void create(),
          onRestore: restore,
        });
      } catch (e) {
        paint();
        fail(e);
      }
    })();
  };

  const unmount = () => h.remove();
  unmount.refresh = () => paint();
  return unmount;
}
